import React, { useMemo } from 'react';
import { Box, Button, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useSelector } from 'react-redux';
import { ReactComponent as DenyIcon } from './icon/deny-ico.svg';
import { ReactComponent as NotFoundIcon } from './icon/404-ico.svg';
import { ReactComponent as ServerErrorIcon } from './icon/500-ico.svg';
import RouteLink from 'components/common/route-link/RouteLink';

interface PageErrorBlockProps {
    errorType?: string;
    showBackHomeBtn?: boolean;
    additionalButtonsSlot?: React.JSX.Element;
    iconImageUrl?: string;
    errorTitle?: string;
    errorMessage?: string;
    errorCssClass?: string;
    errorList?: string[];
}

export default function PageErrorBlock(props: PageErrorBlockProps) {
    const { t } = useBundleTranslation(['components/error/page_error_block']);
    const {
        errorType,
        showBackHomeBtn = true,
        additionalButtonsSlot = null,
        iconImageUrl,
        errorTitle,
        errorMessage,
        errorCssClass,
        errorList,
    } = props;

    const supportAdmins = useSelector((state: any) => state.auth?.userInfo?.supportAdmins) ?? [];

    const IconComponent = useMemo(() => {
        if (iconImageUrl) return <Box component="img" src={iconImageUrl} />;

        if (errorType === 'not_found' || errorType === 'notfound') return <NotFoundIcon />;

        if (errorType === 'internal_server') return <ServerErrorIcon />;

        return <DenyIcon />;
    }, [errorType, iconImageUrl]);

    const ErrorMessageComponent = useMemo(() => {
        if (errorType) {
            return (
                <>
                    {errorTitle && <Box className={'page-error-block__title'}>{errorTitle}</Box>}
                    {errorMessage && (
                        <Box className={'page-error-block__message'}>
                            <Box className={errorCssClass ?? undefined}>
                                <Box
                                    className={'page-error-block__message-holder'}
                                    component={'span'}
                                    dangerouslySetInnerHTML={{ __html: errorMessage }}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            );
        }

        if (!errorList) return null;
        return (
            <>
                {errorList.map((item, index) => {
                    //todo @htmlentities // htmlspecialchars
                    return <Box key={index}>{item}</Box>;
                })}
            </>
        );
    }, [errorType, errorTitle, errorMessage, errorCssClass, errorList]);

    return (
        <Box component="main" className={'page-error-block'}>
            <Stack direction="row" justifyContent="space-between" className={'page-error-block__data-wrapper'}>
                <Stack spacing={1} className={'page-error-block__message-container'}>
                    {ErrorMessageComponent}

                    {supportAdmins.length > 0 && (
                        <Box className={'page-error-block__support-admins-container'}>
                            <Box className={'page-error-block__support-admins-label'}>
                                {t('access_support_administrators')}
                            </Box>
                            <Stack spacing={1} className={'page-error-block__support-admins-list'}>
                                {supportAdmins.map((user: any) => {
                                    return (
                                        <Stack direction="row" key={user.username}>
                                            <Box className={'page-error-block__support-admins-icon'}>
                                                <IconMi icon={'user-profile'} />
                                            </Box>
                                            <Box>{user.display_name}</Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    <Stack
                        className={'page-error-block__buttons-container'}
                        direction={'row'}
                        spacing={0.5}
                        alignItems={'center'}
                    >
                        {showBackHomeBtn && (
                            <Button
                                data-test={'return-button'}
                                component={RouteLink}
                                to={'/home'}
                                className={'page-error-block__button'}
                            >
                                {t('return_btn_label')}
                            </Button>
                        )}
                        {additionalButtonsSlot}
                    </Stack>
                </Stack>
                <Box className={'page-error-block__icon'}>{IconComponent}</Box>
            </Stack>
        </Box>
    );
}
